import type { Variant } from "@shopify/hydrogen";
import { useProduct } from "@shopify/hydrogen/client";
import classNames from "classnames";
import { useEffect, useMemo } from "react";

import { useCartUI } from "../../hooks/use-cart-ui";
import type { CheckoutEnabledConfig } from "../../lookbook";

export interface ProductOptionsProps {
  checkout: CheckoutEnabledConfig;
}

function serializeOptionsToHash(options: Variant["selectedOptions"]) {
  return options
    .map((o) => `${o.name.toLowerCase()}:${o.value.toLowerCase()}`)
    .sort()
    .join(",");
}

export function ProductOptions(props: ProductOptionsProps) {
  const { checkout } = props;

  const { setIsCurrentOptionAvailable } = useCartUI();

  const product = useProduct();

  useEffect(() => {
    const firstAvailable = product?.variants.find((v) => v.availableForSale);

    if (firstAvailable) {
      product?.setSelectedVariant(firstAvailable);
      product?.setSelectedOptions(
        Object.fromEntries(
          firstAvailable.selectedOptions.map((o) => [o.name, o.value]),
        ),
      );
    }
  }, []);

  const variantsByOptions = useMemo(() => {
    if (!product) {
      return null;
    }

    const { variants } = product;

    const variantsByOptionsMap = new Map<string, Variant>();

    const inexactVariantsByOptionsMap = new Map<string, Variant>();

    for (const variant of variants) {
      const { selectedOptions } = variant;

      const optionsHash = serializeOptionsToHash(selectedOptions);
      variantsByOptionsMap.set(optionsHash, variant);

      const inexactOptionsHash = serializeOptionsToHash(
        selectedOptions.filter(
          (o) =>
            !checkout.hiddenProductOptionKeys.includes(o.name.toLowerCase()),
        ),
      );
      inexactVariantsByOptionsMap.set(inexactOptionsHash, variant);
    }

    return { variantsByOptionsMap, inexactVariantsByOptionsMap };
  }, [checkout.hiddenProductOptionKeys, product]);

  const selectedOptionsList = useMemo(() => {
    if (!product) {
      return null;
    }

    return Object.entries(product.selectedOptions).map(
      ([optionName, optionValue]) => {
        return { name: optionName, value: optionValue };
      },
    );
  }, [product]);

  useEffect(() => {
    if (!product || !variantsByOptions || !selectedOptionsList) {
      return;
    }

    const selectedOptionsHash = serializeOptionsToHash(selectedOptionsList);

    const matchingVariant =
      variantsByOptions.variantsByOptionsMap.get(selectedOptionsHash);

    setIsCurrentOptionAvailable(matchingVariant !== undefined);
  }, [
    product,
    variantsByOptions,
    selectedOptionsList,
    setIsCurrentOptionAvailable,
  ]);

  if (!product) {
    return null;
  }

  const {
    options,
    selectedVariant,
    selectedOptions,
    setSelectedOption,
    setSelectedOptions,
    setSelectedVariant,
  } = product;

  if (!selectedVariant || !variantsByOptions || !selectedOptionsList) {
    return null;
  }

  return (
    <div className="item--options">
      {options.map((option) => {
        if (
          checkout.hiddenProductOptionKeys.includes(option.name.toLowerCase())
        ) {
          return null;
        }

        return (
          <fieldset key={`${option.name}--${selectedVariant.id}`}>
            <legend className="mb-2 text-sm uppercase tracking-wide font-medium text-gray-900">
              {option.name}
            </legend>
            <div className="grid grid-cols-3 gap-2">
              {option.values.map((value) => {
                const id = `option-${option.name}-${value}-${selectedVariant.id}`;

                const currentSelectedOptions = selectedOptionsList.map(
                  ({ name: optionName, value: optionValue }) => {
                    if (
                      optionName.toLowerCase() === option.name.toLowerCase()
                    ) {
                      return { name: optionName.toLowerCase(), value };
                    }

                    return { name: optionName, value: optionValue };
                  },
                );

                const currentInexactSelelectedOptions =
                  currentSelectedOptions.filter(
                    (o) =>
                      !checkout.hiddenProductOptionKeys.includes(
                        o.name.toLowerCase(),
                      ),
                  );

                const exactMatchingVariant =
                  variantsByOptions.variantsByOptionsMap?.get(
                    serializeOptionsToHash(currentSelectedOptions),
                  );

                const inexactMatchingVariant =
                  variantsByOptions.inexactVariantsByOptionsMap?.get(
                    serializeOptionsToHash(currentInexactSelelectedOptions),
                  );

                return (
                  <ProductOption
                    key={id}
                    id={id}
                    name={option.name}
                    value={value}
                    checked={selectedOptions[option.name] === value}
                    /** @note use inexact match here because not tracking hidden opts */
                    matchingVariant={inexactMatchingVariant}
                    onSelect={() => {
                      if (exactMatchingVariant) {
                        setSelectedVariant(exactMatchingVariant);
                        setSelectedOption(option.name, value);
                      } else {
                        const variantsByOptionsList = [
                          ...variantsByOptions.variantsByOptionsMap,
                        ];
                        const selectedOptionHash = serializeOptionsToHash([
                          { name: option.name, value: value },
                        ]);
                        const validVariantsWithSelectedOption =
                          variantsByOptionsList.filter(([hash]) =>
                            hash.includes(selectedOptionHash),
                          );

                        if (validVariantsWithSelectedOption.length > 0) {
                          const currentMatchingVariant =
                            validVariantsWithSelectedOption.filter((v) =>
                              currentInexactSelelectedOptions.every(
                                (o) =>
                                  v[0].includes(o.name.toLowerCase()) &&
                                  v[0].includes(o.value.toLowerCase()),
                              ),
                            );

                          if (
                            currentMatchingVariant.length &&
                            currentMatchingVariant[0][1].availableForSale
                          ) {
                            setSelectedVariant(currentMatchingVariant[0][1]);
                            setSelectedOptions(
                              Object.fromEntries(
                                currentMatchingVariant[0][1].selectedOptions.map(
                                  (o) => [o.name, o.value],
                                ),
                              ),
                            );
                          } else {
                            const firstAvailableFallback =
                              validVariantsWithSelectedOption.find(
                                (v) => v[1].availableForSale,
                              );

                            if (firstAvailableFallback) {
                              setSelectedVariant(firstAvailableFallback[1]);
                              setSelectedOptions(
                                Object.fromEntries(
                                  firstAvailableFallback[1].selectedOptions.map(
                                    (o) => [o.name, o.value],
                                  ),
                                ),
                              );
                            } else {
                              setSelectedOption(option.name, value);
                            }
                          }
                        }
                      }
                    }}
                  />
                );
              })}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}

function ProductOption(props: {
  id: string;
  checked: boolean;
  matchingVariant: Variant | undefined;
  name: string;
  value: string;
  onSelect: () => void;
}) {
  const { id, checked, name, value, matchingVariant, onSelect } = props;

  return (
    <label
      htmlFor={id}
      className="transition focus-within:outline-none focus-within:ring rounded-md">
      <input
        className="sr-only"
        tabIndex={1}
        type="radio"
        id={id}
        name={`option[${name}]`}
        value={value}
        checked={checked}
        onChange={() => {
          onSelect();
        }}
      />
      <div
        className={classNames(
          "transition rounded-md py-3 px-3 flex items-center justify-center text-sm font-medium uppercase sm:flex-1 cursor-pointer border-2 h-full text-center",
          {
            "bg-black border-transparent text-white hover:bg-neutral-900":
              checked,
            "bg-white border-neutral-300 text-neutral-900 hover:bg-neutral-50":
              !checked,
            "bg-neutral-300 border-neutral-300 text-white hover:bg-neutral-50":
              !checked && matchingVariant === undefined,
          },
        )}>
        {value}
      </div>
    </label>
  );
}
